/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect, useMemo, useState } from 'react';
import { PdfViewer } from './PdfViewer';
import { makePearlApiFhirCallAndUnwrap } from './fhir_network';
import { IAttachment, IBinary } from '@ahryman40k/ts-fhir-types/lib/R4';
import { WideTooltip } from '../chat/components/WideTooltip';

export default function PresentedFormViewTooltip({
  presentedForm,
  mrn,
  server,
  children,
}: {
  presentedForm: IAttachment;
  mrn: string;
  server: string;
  children: React.ReactElement;
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<string | undefined>(presentedForm.data ? atob(presentedForm.data) : undefined);

  const controller: AbortController = useMemo(() => new AbortController(), []);
  useEffect(() => () => controller.abort(), [controller]);
  const url = presentedForm.url ? `/fhir/${presentedForm.url}` : undefined;

  const handleOpen = async () => {
    console.debug('PresentedFormViewTooltip: handleOpen called');
    if (!data && !loading && url) {
      try {
        setLoading(true);
        console.debug('PresentedFormViewTooltip: fetching', url);
        const result = await makePearlApiFhirCallAndUnwrap<IBinary>(url, mrn, server, controller.signal);
        setData(typeof result === 'object' ? result.data : (result as unknown as string));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <WideTooltip
      onOpen={handleOpen}
      title={
        data ? (
          <PresentedFormContent contentType={presentedForm.contentType} content={data} />
        ) : loading ? (
          'Loading...'
        ) : (
          'Hover to load'
        )
      }
      arrow
    >
      {/* Ensure a real DOM element child so MUI can attach listeners/ref */}
      <span style={{ display: 'inline-block' }}>{children}</span>
    </WideTooltip>
  );
}

function PresentedFormContent({ contentType, content }: { contentType?: string; content: string }) {
  if (
    contentType === 'application/pdf' ||
    contentType === 'application/x-pdf' ||
    (!contentType && content.startsWith('%PDF-'))
  ) {
    return <PdfViewer content={content} />;
  }
  if (
    contentType === 'text/html' ||
    (!contentType && (content.startsWith('<!DOCTYPE html>') || content.startsWith('<html')))
  ) {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return <pre>{content}</pre>;
}
